import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  LogOut,
  Video,
  Bell,
  Headphones,
  BookOpen,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import VideoGallery from "./VideoGallery";
import AnnouncementsSection from "./AnnouncementsSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Admin Management Component for Lectures
const LectureManagement = ({ token }) => {
  const [lectures, setLectures] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    speaker: "",
    duration: "",
    category: "Adhurime",
    description: "",
    audio_url: "",
    video_url: "",
    cover_color: "from-emerald-500 to-teal-600",
  });

  const categories = ["Adhurime", "Ahlak", "Sire", "Familje", "Akide"];
  const colorOptions = [
    { value: "from-emerald-500 to-teal-600", label: "Jeshile" },
    { value: "from-blue-500 to-indigo-600", label: "Blu" },
    { value: "from-purple-500 to-pink-600", label: "Vjollcë" },
    { value: "from-amber-500 to-orange-600", label: "Portokalli" },
    { value: "from-rose-500 to-red-600", label: "Kuqe" },
    { value: "from-cyan-500 to-blue-600", label: "Cyan" },
  ];

  useEffect(() => {
    fetchLectures();
  }, []);

  const fetchLectures = async () => {
    try {
      const res = await axios.get(`${API}/lectures`);
      setLectures(res.data);
    } catch (error) {
      console.error("Error fetching lectures:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/lectures`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ligjërata u shtua me sukses!");
      setFormData({
        title: "",
        speaker: "",
        duration: "",
        category: "Adhurime",
        description: "",
        audio_url: "",
        video_url: "",
        cover_color: "from-emerald-500 to-teal-600",
      });
      setShowForm(false);
      fetchLectures();
    } catch (error) {
      toast.error("Gabim gjatë shtimit të ligjëratës");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Jeni të sigurt që doni të fshini këtë ligjëratë?"))
      return;
    try {
      await axios.delete(`${API}/admin/lectures/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ligjërata u fshi!");
      fetchLectures();
    } catch (error) {
      toast.error("Gabim gjatë fshirjes");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          Ligjëratat ({lectures.length})
        </h3>
        <Button onClick={() => setShowForm(!showForm)} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Shto Ligjëratë
        </Button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit}
            className="bg-gray-50 rounded-xl p-4 space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Titulli</Label>
                <Input
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Rëndësia e Namazit"
                  required
                />
              </div>
              <div>
                <Label>Ligjëruesi</Label>
                <Input
                  value={formData.speaker}
                  onChange={(e) =>
                    setFormData({ ...formData, speaker: e.target.value })
                  }
                  placeholder="Hoxhë ..."
                  required
                />
              </div>
              <div>
                <Label>Kohëzgjatja</Label>
                <Input
                  value={formData.duration}
                  onChange={(e) =>
                    setFormData({ ...formData, duration: e.target.value })
                  }
                  placeholder="45:30"
                  required
                />
              </div>
              <div>
                <Label>Kategoria</Label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Përshkrimi</Label>
                <Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Përshkrim i shkurtër..."
                  required
                />
              </div>
              <div>
                <Label>URL e Audios</Label>
                <Input
                  value={formData.audio_url}
                  onChange={(e) =>
                    setFormData({ ...formData, audio_url: e.target.value })
                  }
                  placeholder="https://... (opsionale nëse keni video)"
                />
              </div>
              <div>
                <Label>URL e Videos (YouTube)</Label>
                <Input
                  value={formData.video_url}
                  onChange={(e) =>
                    setFormData({ ...formData, video_url: e.target.value })
                  }
                  placeholder="https://youtube.com/watch?v=... (opsionale)"
                />
              </div>
              <div>
                <Label>Ngjyra</Label>
                <select
                  value={formData.cover_color}
                  onChange={(e) =>
                    setFormData({ ...formData, cover_color: e.target.value })
                  }
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  {colorOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? "Duke shtuar..." : "Shto"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Anulo
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {lectures.map((lecture) => (
          <div
            key={lecture.id}
            className="flex items-center justify-between p-3 bg-white border rounded-lg"
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-lg bg-gradient-to-br ${lecture.cover_color} flex items-center justify-center`}
              >
                <Headphones className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium">{lecture.title}</p>
                <p className="text-sm text-gray-500">
                  {lecture.speaker} • {lecture.duration}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(lecture.id)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Admin Management Component for Duas
const DuaManagement = ({ token }) => {
  const [duas, setDuas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: "morning",
    title: "",
    arabic: "",
    albanian: "",
    source: "",
    benefits: "",
  });

  const categories = [
    { value: "morning", label: "Mëngjes" },
    { value: "evening", label: "Mbrëmje" },
    { value: "prayer", label: "Namaz" },
    { value: "food", label: "Ushqim" },
    { value: "travel", label: "Udhëtim" },
    { value: "protection", label: "Mbrojtje" },
    { value: "forgiveness", label: "Falje" },
    { value: "daily", label: "Ditore" },
  ];

  useEffect(() => {
    fetchDuas();
  }, []);

  const fetchDuas = async () => {
    try {
      const res = await axios.get(`${API}/duas`);
      setDuas(res.data);
    } catch (error) {
      console.error("Error fetching duas:", error);
    }
  };

  const handleTranslate = async () => {
    if (!formData.albanian || formData.arabic) return;
    try {
      const res = await axios.post(`${API}/admin/translate`, {
        text: formData.albanian,
        source: "sq",
        target: "ar",
      });
      setFormData((prev) => ({ ...prev, arabic: res.data.translated }));
    } catch (error) {
      console.error("Translation error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Re-add dummy transliteration if required by backend schema
      const submitData = { ...formData, transliteration: "" };
      await axios.post(`${API}/admin/duas`, submitData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Duaja u shtua me sukses!");
      setFormData({
        category: "morning",
        title: "",
        arabic: "",
        albanian: "",
        source: "",
        benefits: "",
      });
      setShowForm(false);
      fetchDuas();
    } catch (error) {
      toast.error("Gabim gjatë shtimit të duasë");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Jeni të sigurt që doni të fshini këtë dua?")) return;
    try {
      await axios.delete(`${API}/admin/duas/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Duaja u fshi!");
      fetchDuas();
    } catch (error) {
      toast.error("Gabim gjatë fshirjes");
    }
  };

  const getCategoryLabel = (value) => {
    return categories.find((c) => c.value === value)?.label || value;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Duatë ({duas.length})</h3>
        <Button onClick={() => setShowForm(!showForm)} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Shto Dua
        </Button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit}
            className="bg-gray-50 rounded-xl p-4 space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Titulli</Label>
                <Input
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Duaja e Mëngjesit"
                  required
                />
              </div>
              <div>
                <Label>Kategoria</Label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Label>
                  Përkthimi Shqip (Shkruaj këtu për përkthim automatik)
                </Label>
                <Input
                  value={formData.albanian}
                  onChange={(e) =>
                    setFormData({ ...formData, albanian: e.target.value })
                  }
                  onBlur={handleTranslate}
                  placeholder="Në emër të Allahut..."
                  required
                />
              </div>
              <div className="col-span-2">
                <Label>Teksti Arab (Përthehet automatikisht)</Label>
                <Input
                  value={formData.arabic}
                  onChange={(e) =>
                    setFormData({ ...formData, arabic: e.target.value })
                  }
                  placeholder="بِسْمِ اللَّهِ..."
                  dir="rtl"
                  required
                />
              </div>
              <div>
                <Label>Burimi</Label>
                <Input
                  value={formData.source}
                  onChange={(e) =>
                    setFormData({ ...formData, source: e.target.value })
                  }
                  placeholder="Buhariu"
                  required
                />
              </div>
              <div>
                <Label>Dobitë</Label>
                <Input
                  value={formData.benefits}
                  onChange={(e) =>
                    setFormData({ ...formData, benefits: e.target.value })
                  }
                  placeholder="Mbrojtje gjatë ditës"
                  required
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? "Duke shtuar..." : "Shto"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Anulo
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {duas.map((dua) => (
          <div
            key={dua.id}
            className="flex items-center justify-between p-3 bg-white border rounded-lg"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  {getCategoryLabel(dua.category)}
                </span>
                <p className="font-medium truncate">{dua.title}</p>
              </div>
              <p className="text-sm text-gray-500 truncate" dir="rtl">
                {dua.arabic}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(dua.id)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Admin Management Component for Articles
const ArticleManagement = ({ token }) => {
  const [articles, setArticles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/admin/articles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArticles(res.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/articles`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Artikulli u shtua me sukses!");
      setFormData({ title: "", content: "" });
      setShowForm(false);
      fetchArticles();
    } catch (error) {
      toast.error("Gabim gjatë shtimit të artikullit");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Jeni të sigurt që doni të fshini këtë artikull?"))
      return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Artikulli u fshi!");
      fetchArticles();
    } catch (error) {
      toast.error("Gabim gjatë fshirjes");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Artikujt ({articles.length})</h3>
        <Button onClick={() => setShowForm(!showForm)} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Shto Artikull
        </Button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit}
            className="bg-gray-50 rounded-xl p-4 space-y-4 overflow-hidden"
          >
            <div className="space-y-4">
              <div>
                <Label>Titulli</Label>
                <Input
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Titulli i artikullit"
                  required
                />
              </div>
              <div>
                <Label>Përmbajtja</Label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  placeholder="Shkruani përmbajtjen e artikullit këtu..."
                  className="w-full min-h-[250px] p-3 rounded-md border border-input bg-background"
                  required
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? "Duke ruajtur..." : "Ruaj Artikullin"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                 Anulo
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {articles.map((article) => (
          <div
            key={article.id}
            className="flex items-center justify-between p-3 bg-white border rounded-lg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium line-clamp-1">{article.title}</p>
                <p className="text-sm text-gray-500">
                  {new Date(article.created_at).toLocaleDateString("sq-AL")}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(article.id)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminPanel = ({
  isOpen,
  onClose,
  onLogin,
  onLogout,
  isLoggedIn,
  token,
}) => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/admin/login`, credentials);
      const { token: authToken, username } = res.data;
      onLogin(authToken, username);
      toast.success(`Mirësevini, ${username}!`);
      setCredentials({ username: "", password: "" });
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Të dhënat e hyrjes janë të pasakta");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    onLogout();
    toast.success("Dolët me sukses");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto z-[60]"
        data-testid="admin-panel"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading flex items-center gap-2">
            <Lock className="w-6 h-6 text-primary" />
            Paneli Admin
          </DialogTitle>
        </DialogHeader>

        {!isLoggedIn ? (
          <motion.form
            onSubmit={handleLogin}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6 py-6"
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="username">Emri përdoruesit</Label>
                <Input
                  id="username"
                  value={credentials.username}
                  onChange={(e) =>
                    setCredentials({ ...credentials, username: e.target.value })
                  }
                  placeholder="admin"
                  required
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Fjalëkalimi</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) =>
                    setCredentials({ ...credentials, password: e.target.value })
                  }
                  placeholder="••••••••"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              data-testid="admin-login-button"
            >
              {loading ? "Duke u lidhur..." : "Hyr"}
            </Button>
          </motion.form>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-6"
          >
            <div className="flex justify-between items-center mb-6">
              <p className="text-muted-foreground">
                Ju jeni të lidhur si{" "}
                <span className="font-semibold text-primary">
                  Administrator
                </span>
              </p>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="admin-logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Dil
              </Button>
            </div>

            <Tabs defaultValue="videos" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="videos" data-testid="admin-videos-tab">
                  <Video className="w-4 h-4 mr-2" />
                  Videot
                </TabsTrigger>
                <TabsTrigger
                  value="announcements"
                  data-testid="admin-announcements-tab"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Njoftimet
                </TabsTrigger>
                <TabsTrigger value="lectures" data-testid="admin-lectures-tab">
                  <Headphones className="w-4 h-4 mr-2" />
                  Ligjëratat
                </TabsTrigger>
                <TabsTrigger value="duas" data-testid="admin-duas-tab">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Duatë
                </TabsTrigger>
                <TabsTrigger value="articles" data-testid="admin-articles-tab">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Artikujt
                </TabsTrigger>
              </TabsList>
              <TabsContent value="videos" className="mt-6">
                <VideoGallery isAdmin={true} token={token} />
              </TabsContent>
              <TabsContent value="announcements" className="mt-6">
                <AnnouncementsSection isAdmin={true} token={token} />
              </TabsContent>
              <TabsContent value="lectures" className="mt-6">
                <LectureManagement token={token} />
              </TabsContent>
              <TabsContent value="duas" className="mt-6">
                <DuaManagement token={token} />
              </TabsContent>
              <TabsContent value="articles" className="mt-6">
                <ArticleManagement token={token} />
              </TabsContent>
            </Tabs>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminPanel;
